import type { EnsuredSidekickConversation } from '../contexts/SidekickApiContext';
import type {
  Conversation,
  ErrorEvent,
  Message,
  SidekickError,
  TransportRequestContext,
} from '../state/chatTypes';
import { classifySidekickErrorPresentation } from '../state/errorClassification';
import { generateMessageId } from '../utils';
import type { ConversationTransportTurn } from './useConversation.transport.helpers';

const EMPTY_CONVERSATION_CREATED_AT = '1970-01-01T00:00:00.000Z';

/**
 * Builds the stable conversation view returned before hydration completes.
 * @param conversation - Hydrated conversation, when available
 * @param messages - Messages currently held by the chat slice
 * @returns The hydrated conversation or an empty-metadata fallback
 */
export const buildConversationView = (
  conversation: Conversation | null,
  messages: Message[]
): Conversation => {
  if (conversation !== null) {
    return conversation;
  }
  return {
    conversationId: '',
    messages,
    messageCount: messages.length,
    createdAt: EMPTY_CONVERSATION_CREATED_AT,
    updatedAt: EMPTY_CONVERSATION_CREATED_AT,
  };
};

/**
 * Builds the initial chat state for a newly created backend conversation.
 * @param ensured - Identity returned by the conversation creator
 * @param messages - Messages accumulated before creation completed
 * @returns Conversation ready for the chat slice
 */
export const buildCreatedConversation = (
  ensured: EnsuredSidekickConversation,
  messages: Message[]
): Conversation => ({
  conversationId: ensured.conversationId,
  messages,
  messageCount: messages.length,
  createdAt: ensured.createdAt,
  updatedAt: ensured.createdAt,
});

/**
 * Builds one transport turn with persisted retry context taking precedence.
 * @param content - Trimmed user prompt
 * @param conversationId - Target backend conversation
 * @param requestContext - Context retained by a retried error
 * @param fleetId - Current host fleet fallback
 * @param interfaceName - Current host interface fallback
 * @returns Transport input for the selected Sidekick client
 */
export const buildConversationTransportTurn = (
  content: string,
  conversationId: string,
  requestContext: TransportRequestContext | undefined,
  fleetId: string | undefined,
  interfaceName: string | undefined
): ConversationTransportTurn => ({
  content,
  conversationId,
  fleetId: requestContext === undefined ? fleetId : requestContext.fleetId,
  interfaceName:
    requestContext === undefined ? interfaceName : requestContext.interfaceName,
});

/**
 * Builds a user-role message for dispatch into the chat slice.
 * @param conversationId - Active conversation id
 * @param content - Trimmed user prompt text
 */
export const buildUserMessage = (
  conversationId: string,
  content: string
): Message => ({
  id: generateMessageId(),
  conversationId,
  role: 'user',
  content,
  createdAt: new Date().toISOString(),
});

/**
 * Maps a transport / BE error into an inline `ErrorEvent` payload.
 * @param sourceUserMessageId - Id of the user message that triggered the turn
 * @param error - Typed Sidekick error from the transport layer
 * @param requestContext - Fleet and interface context from the failed turn
 */
export const buildErrorEvent = (
  sourceUserMessageId: string,
  error: SidekickError,
  requestContext?: TransportRequestContext
): ErrorEvent => {
  const presentation = classifySidekickErrorPresentation(error);
  return {
    id: generateMessageId(),
    sourceUserMessageId,
    variant: presentation.variant,
    canRetry: presentation.canRetry,
    ...(requestContext !== undefined ? { requestContext } : {}),
    createdAt: new Date().toISOString(),
  };
};
